/**
 * Сторінка «Прогнози» — порівняння методик прогнозу РДН-ціни з фактом.
 *
 * Шапка (дата + ‹ ›) як на «План v2». Широка таблиця: для кожної методики —
 * три стовпці (прогноз / різниця факт−прогноз / % похибки зі знаком).
 * Нижче — добове зведення похибок і рейтинг точності за період.
 *
 * Фаза A: працюють статистичні методи (avg7d, dow4w). Погодні методики
 * присутні як стовпці, але повертають прочерки (вмикаються у Фазах B/C).
 */
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, HelpCircle } from "lucide-react";

import {
  type MethodMeta,
  type MethodSummary,
  rdnForecastsApi,
} from "@/api/rdnForecasts";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { fmt, hourLabelShifted, num, todayIso } from "@/lib/format";
import { cn } from "@/lib/utils";

function shiftDate(iso: string, deltaDays: number): string {
  const d = new Date(iso);
  d.setDate(d.getDate() + deltaDays);
  return d.toISOString().slice(0, 10);
}

/** Колір клітинки за абсолютною похибкою у %. */
function errColor(absPct: number | null): string {
  if (absPct === null) return "text-slate-400";
  if (absPct <= 5) return "text-emerald-700";
  if (absPct <= 15) return "text-amber-700";
  return "text-red-700";
}

export function ForecastsPage() {
  const { t } = useTranslation();
  const [date, setDate] = useState(todayIso());

  const compQ = useQuery({
    queryKey: ["rdn-comparison", date],
    queryFn: () => rdnForecastsApi.comparison(date),
  });

  const [accFrom, setAccFrom] = useState(() => shiftDate(todayIso(), -30));
  const [accTo, setAccTo] = useState(() => todayIso());
  const accQ = useQuery({
    queryKey: ["rdn-accuracy", accFrom, accTo],
    queryFn: () => rdnForecastsApi.accuracy(accFrom, accTo),
  });

  // Solcast приховуємо у UI (free-tier вичерпано; бекенд лишається без змін).
  // Коли повернемось до Solcast — просто прибрати ключі з цього set.
  const HIDDEN_METHODS = new Set([
    "weather_heuristic_solcast",
    "weather_regression_solcast",
  ]);
  const methods = (compQ.data?.methods ?? []).filter((m) => !HIDDEN_METHODS.has(m.key));
  const summaryByKey = useMemo(() => {
    const m = new Map<string, MethodSummary>();
    for (const s of compQ.data?.day_summary ?? []) m.set(s.method_key, s);
    return m;
  }, [compQ.data]);

  return (
    <div className="space-y-4">
      {/* Шапка */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <CardTitle className="min-w-0">{t("forecasts.title")}</CardTitle>
            <HelpHint />
            <CardDescription className="basis-full">{t("forecasts.subtitle")}</CardDescription>
          </div>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" onClick={() => setDate((d) => shiftDate(d, -1))}
              aria-label={t("home1.prev_day")} title={t("home1.prev_day")}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-[150px]" />
            <Button variant="outline" size="sm" onClick={() => setDate((d) => shiftDate(d, 1))}
              aria-label={t("home1.next_day")} title={t("home1.next_day")}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Погодинна таблиця */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {compQ.isLoading ? (
            <div className="p-6 text-sm text-slate-500">{t("common.loading")}</div>
          ) : (
            <table className="text-sm">
              <thead className="bg-slate-50">
                <tr className="text-slate-500">
                  <th className="p-2 text-left sticky left-0 bg-slate-50 z-10">{t("common.hour")}</th>
                  <th className="p-2 text-right border-r border-slate-200">{t("forecasts.fact")}</th>
                  {methods.map((m) => (
                    <th key={m.key} colSpan={3}
                      className={cn("p-2 text-center border-r border-slate-200", !m.available && "text-slate-400")}>
                      {t(m.label_i18n)}
                      {!m.available && <span className="ml-1 text-[10px]">({t("forecasts.soon")})</span>}
                    </th>
                  ))}
                </tr>
                <tr className="text-slate-400 text-[11px]">
                  <th className="p-1 sticky left-0 bg-slate-50 z-10" />
                  <th className="p-1 border-r border-slate-200" />
                  {methods.map((m) => (
                    <FragmentSubHead key={m.key} t={t} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {(compQ.data?.hours ?? []).map((row) => {
                  const fact = num(row.fact);
                  return (
                    <tr key={row.hour} className="border-t border-slate-100">
                      <td className="p-2 font-mono text-xs sticky left-0 bg-white z-10">
                        {hourLabelShifted(row.hour)}
                      </td>
                      <td className="p-2 text-right font-mono border-r border-slate-200">
                        {fact !== null ? fmt(fact) : <span className="text-slate-300">—</span>}
                      </td>
                      {methods.map((m) => {
                        const pred = num(row.forecasts[m.key] ?? null);
                        const diff = fact !== null && pred !== null ? fact - pred : null;
                        const errPct = fact !== null && pred !== null && fact !== 0
                          ? ((fact - pred) / fact) * 100 : null;
                        return (
                          <MethodCells key={m.key} pred={pred} diff={diff} errPct={errPct} />
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
              {/* Зведення за добу: середня |похибка| % і MAE грн */}
              <tfoot className="bg-slate-50 font-medium">
                <tr className="border-t-2 border-slate-200">
                  <td className="p-2 text-left sticky left-0 bg-slate-50 z-10 text-xs">{t("forecasts.day_mape")}</td>
                  <td className="p-2 border-r border-slate-200" />
                  {methods.map((m) => {
                    const s = summaryByKey.get(m.key);
                    const mape = num(s?.mean_abs_error_pct ?? null);
                    const mae = num(s?.mean_abs_error_uah ?? null);
                    return (
                      <td key={m.key} colSpan={3} className="p-2 text-center border-r border-slate-200">
                        {mape !== null ? (
                          <span className={errColor(mape)}>
                            {fmt(mape, 1)}% <span className="text-slate-400">/ {fmt(mae)} {t("forecasts.uah_short")}</span>
                          </span>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Рейтинг точності за період */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("forecasts.ranking_title")}</CardTitle>
          <CardDescription>{t("forecasts.ranking_subtitle")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1 text-xs text-slate-500">
              {t("forecasts.from")}
              <Input type="date" value={accFrom} onChange={(e) => setAccFrom(e.target.value)} className="w-[150px]" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-slate-500">
              {t("forecasts.to")}
              <Input type="date" value={accTo} onChange={(e) => setAccTo(e.target.value)} className="w-[150px]" />
            </label>
          </div>
          <AccuracyTable
            methods={(accQ.data?.methods ?? []).filter((m) => !HIDDEN_METHODS.has(m.key))}
            summary={accQ.data?.summary ?? []}
            loading={accQ.isLoading} t={t} />
        </CardContent>
      </Card>
    </div>
  );
}

function FragmentSubHead({ t }: { t: (k: string) => string }) {
  return (
    <>
      <th className="p-1 text-right">{t("forecasts.col_forecast")}</th>
      <th className="p-1 text-right">{t("forecasts.col_diff")}</th>
      <th className="p-1 text-right border-r border-slate-200">{t("forecasts.col_err")}</th>
    </>
  );
}

function MethodCells({
  pred, diff, errPct,
}: { pred: number | null; diff: number | null; errPct: number | null }) {
  return (
    <>
      <td className="p-2 text-right font-mono text-xs">
        {pred !== null ? fmt(pred) : <span className="text-slate-300">—</span>}
      </td>
      <td className="p-2 text-right font-mono text-xs text-slate-600">
        {diff !== null ? (diff > 0 ? "+" : "") + fmt(diff) : <span className="text-slate-300">—</span>}
      </td>
      <td className={cn("p-2 text-right font-mono text-xs border-r border-slate-200", errColor(errPct === null ? null : Math.abs(errPct)))}>
        {errPct !== null ? (errPct > 0 ? "+" : "") + fmt(errPct, 1) + "%" : <span className="text-slate-300">—</span>}
      </td>
    </>
  );
}

function AccuracyTable({
  methods, summary, loading, t,
}: {
  methods: MethodMeta[];
  summary: MethodSummary[];
  loading: boolean;
  t: (k: string) => string;
}) {
  const byKey = new Map(summary.map((s) => [s.method_key, s]));
  // Найкращий метод = найменший MAPE серед доступних із даними.
  const ranked = methods
    .map((m) => ({ m, mape: num(byKey.get(m.key)?.mean_abs_error_pct ?? null) }))
    .filter((x) => x.mape !== null) as { m: MethodMeta; mape: number }[];
  ranked.sort((a, b) => a.mape - b.mape);
  const bestKey = ranked[0]?.m.key;

  if (loading) return <div className="text-sm text-slate-500">{t("common.loading")}</div>;

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-50">
          <tr className="text-left text-slate-500 text-xs">
            <th className="p-2">{t("forecasts.method_col")}</th>
            <th className="p-2 text-right">{t("forecasts.mape")}</th>
            <th className="p-2 text-right">{t("forecasts.mae")}</th>
            <th className="p-2 text-right">{t("forecasts.bias")}</th>
            <th className="p-2 text-right">{t("forecasts.n_hours")}</th>
          </tr>
        </thead>
        <tbody>
          {methods.map((m) => {
            const s = byKey.get(m.key);
            const mape = num(s?.mean_abs_error_pct ?? null);
            const mae = num(s?.mean_abs_error_uah ?? null);
            const bias = num(s?.mean_signed_error_pct ?? null);
            const isBest = m.key === bestKey;
            return (
              <tr key={m.key} className={cn("border-t border-slate-100", isBest && "bg-emerald-50")}>
                <td className="p-2">
                  {isBest && <span className="mr-1">🏆</span>}
                  <span className={cn(!m.available && "text-slate-400")}>{t(m.label_i18n)}</span>
                </td>
                <td className={cn("p-2 text-right font-mono", errColor(mape))}>
                  {mape !== null ? fmt(mape, 1) + "%" : "—"}
                </td>
                <td className="p-2 text-right font-mono">{mae !== null ? fmt(mae) : "—"}</td>
                <td className="p-2 text-right font-mono text-slate-600">
                  {bias !== null ? (bias > 0 ? "+" : "") + fmt(bias, 1) + "%" : "—"}
                </td>
                <td className="p-2 text-right font-mono text-slate-400">{s?.n_hours ?? 0}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function HelpHint() {
  const { t } = useTranslation();
  const lines = t("forecasts.help.lines", { returnObjects: true }) as string[];
  return (
    <div className="relative group ml-auto">
      <HelpCircle className="h-5 w-5 text-slate-400 group-hover:text-slate-600 cursor-help" />
      <div className="invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity
        absolute right-0 top-7 z-50 w-96 max-w-[90vw] rounded-md border border-slate-200 bg-white p-3
        text-xs leading-relaxed text-slate-600 shadow-xl space-y-1">
        <div className="font-semibold text-slate-800">{t("forecasts.help.title")}</div>
        {Array.isArray(lines) && lines.map((l, i) => (
          <div key={i} className={l === "" ? "h-1" : ""}>{l}</div>
        ))}
      </div>
    </div>
  );
}
